import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import VideoUpload from './VideoUpload';
import Home from './Home';
import SampleAnalysis from './SampleAnalysis';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Horses from './pages/Horses';
import Videos from './pages/Videos';
import Admin from './pages/Admin';
import './App.css';

function RecoveryRedirect({ active }) {
  const location = useLocation();
  if (active && location.pathname !== '/reset-password') {
    return <Navigate to="/reset-password" replace />;
  }
  return null;
}

function NavBar({ user, isAdmin, onSignOut }) {
  const location = useLocation();
  const isActive = (path) => location.pathname === path ? 'nav-link active' : 'nav-link';

  const isHomePage = location.pathname === '/';

  return (
    <nav className="navbar">
      <div className="nav-container">
        <Link to="/" className="nav-brand">
          <img src="/tru_stride_logo.svg" alt="Tru-Stride Logo" className="nav-logo" />
          <div className="nav-brand-text">
            <div className="nav-brand-main">Tru-Stride</div>
            <div className="nav-brand-sub">Equine Gait Analysis</div>
          </div>
        </Link>
        <div className="nav-links">
          {isHomePage ? (
            <>
              <a href="#how-it-works" className="nav-link">How It Works</a>
              <a href="#pricing" className="nav-link">Pricing</a>
              <a href="#about" className="nav-link">About</a>
              <a href="#faqs" className="nav-link">FAQs</a>
              <a href="#waitlist" className="nav-link nav-cta">Join waitlist</a>
            </>
          ) : (
            <>
              <Link to="/" className={isActive('/')}>Home</Link>
              {user ? (
                <>
                  <Link to="/dashboard" className={isActive('/dashboard')}>Dashboard</Link>
                  <Link to="/videos" className={isActive('/videos')}>My Videos</Link>
                  <Link to="/horses" className={isActive('/horses')}>My Horses</Link>
                  <Link to="/profile" className={isActive('/profile')}>Profile</Link>
                  {isAdmin && <Link to="/admin" className={isActive('/admin')}>Admin</Link>}
                  <button onClick={onSignOut} className="nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}>Sign Out</button>
                </>
              ) : (
                <Link to="/login" className="nav-link nav-cta">Sign In</Link>
              )}
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  useEffect(() => {
    // Ensure a profiles row exists for the authenticated user
    const ensureProfile = async (user) => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      const { data } = await supabase
        .from('profiles')
        .select('id, is_admin')
        .eq('id', user.id)
        .single();
      const knownAdmin = (user.email || '').toLowerCase() === 'jphorselver@gmail.com';
      setIsAdmin(Boolean(data?.is_admin) || knownAdmin);
      if (!data) {
        await supabase.rpc('create_user_profile', {
          user_id: user.id,
          user_email: user.email,
          user_full_name:
            user.user_metadata?.full_name
            || [user.user_metadata?.first_name, user.user_metadata?.last_name].filter(Boolean).join(' ')
            || '',
        });
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      ensureProfile(currentUser);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      if (event === 'USER_UPDATED' || event === 'SIGNED_OUT') setPasswordRecovery(false);
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      ensureProfile(currentUser);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const ProtectedRoute = ({ children }) => {
    if (loading) return <div style={{ textAlign: 'center', padding: '80px', color: '#8b6914' }}>Loading...</div>;
    if (!user) return <Navigate to="/login" replace />;
    return children;
  };

  return (
    <Router>
      <div className="app-wrapper">
        <NavBar user={user} isAdmin={isAdmin} onSignOut={handleSignOut} />
        <RecoveryRedirect active={passwordRecovery} />
        <main className="main-content">
          <Routes>
            {/* Original pages */}
            <Route path="/" element={<Home />} />
            <Route path="/analyze" element={<ProtectedRoute><VideoUpload user={user} /></ProtectedRoute>} />
            <Route path="/sample" element={<SampleAnalysis />} />

            {/* Auth pages */}
            <Route path="/login" element={user && !passwordRecovery ? <Navigate to="/dashboard" replace /> : <Login />} />
            <Route path="/signup" element={user && !passwordRecovery ? <Navigate to="/dashboard" replace /> : <Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Protected pages */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard user={user} /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile user={user} /></ProtectedRoute>} />
            <Route path="/videos" element={<ProtectedRoute><Videos user={user} /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><Admin user={user} /></ProtectedRoute>} />
            <Route path="/horses" element={<ProtectedRoute><Horses user={user} /></ProtectedRoute>} />
            <Route path="/horses/new" element={<ProtectedRoute><Horses user={user} /></ProtectedRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <footer className="footer">
          <div className="footer-container">
            <div className="footer-brand">
              <img src="/tru_stride_logo.svg" alt="Tru-Stride" className="footer-logo" />
              <span>Tru-Stride</span>
            </div>
            <p className="footer-tagline">Expert gait analysis for every horse owner.</p>
            <p className="footer-note">Not veterinary coaching. Educational insights to support your horsemanship.</p>
          </div>
        </footer>
      </div>
    </Router>
  );
}

export default App;
